import classNames from 'classnames';
import L from 'leaflet';
import React, { memo, useMemo } from 'react';
import { TileLayer, Polygon, Marker } from 'react-leaflet';

import { LAYER_PARAMS, getCacheKey, getPolygonBounds } from '~/helpers/supply';

import { playClanBaseOver, stopClanBaseOver } from '~/web2ClientAPI/sounds';

import BuildingTooltip from '~/Components/ClanSupply/BuildingTooltip/BuildingTooltip';

import styles from './BuildingLayer.module.scss';
import markers from '../markers/markers';

import type { BUILDINGS } from '~/Actions/ActionSupply';
import type { ISupplyBuilding } from '~/store/slices/settingsSlice';
import type { IBattlesStats } from '~/types/declaration';
import type { Nullable } from '~/types/generics';

const SHIP_LAYERS = ['vessels', 'ships'];

type IBuildingLayerProps = {
  battlesStats: IBattlesStats;
  building: ISupplyBuilding;
  highlightDelay: number;
  isEdgesVisible: boolean;
  isMapBlured: boolean;
  selectedBuildingId: Nullable<BUILDINGS>;
  confirmBuildingId: Nullable<BUILDINGS>;
  tempHoverBuildingLevel: Nullable<number>;
  zoom: number;
  highlightTimers: React.MutableRefObject<Partial<Record<BUILDINGS, ReturnType<typeof setTimeout>>>>;
  tooltipTimers: React.MutableRefObject<Partial<Record<BUILDINGS, ReturnType<typeof setTimeout>>>>;
  setHighlightedBuilding: (buildingId: Nullable<BUILDINGS>) => void;
  selectBuilding: (buildingId: BUILDINGS, needSavePosition?: boolean) => void;
};

const BuildingLayer: React.FC<IBuildingLayerProps> = ({
  battlesStats,
  building,
  highlightDelay,
  highlightTimers,
  isEdgesVisible,
  isMapBlured,
  selectedBuildingId,
  confirmBuildingId,
  tempHoverBuildingLevel,
  tooltipTimers,
  zoom,
  selectBuilding,
  setHighlightedBuilding,
}) => {
  const buildingId = building.name;
  const level = building.level;
  const levelLayer = building.levels[level];

  const isSelected = selectedBuildingId === buildingId;
  const isConfirmBuilding = confirmBuildingId === buildingId;

  const tileLayerBounds = useMemo(() => {
    if (levelLayer?.geo?.edges) {
      const cacheKey = getCacheKey(buildingId, level);
      return getPolygonBounds(cacheKey, levelLayer.geo.edges);
    }
    return;
  }, [buildingId, level, levelLayer.geo?.edges]);

  const edgePositions = useMemo(() => {
    if (tileLayerBounds) {
      const bounds = tileLayerBounds;
      return [
        [bounds.getNorthEast().lat, bounds.getNorthEast().lng],
        [bounds.getSouthWest().lat, bounds.getNorthEast().lng],
        [bounds.getSouthWest().lat, bounds.getSouthWest().lng],
        [bounds.getNorthEast().lat, bounds.getSouthWest().lng],
      ] as L.LatLngExpression[];
    }
    return undefined;
  }, [tileLayerBounds]);

  const layers: React.ReactNode[] = [];

  // Render TileLayer
  if (levelLayer?.url) {
    if (level > 0 || isSelected) {
      let opacity = 1;
      if (isMapBlured) {
        if (!isSelected) {
          opacity = 0;
        } else if (!isConfirmBuilding) {
          if (tempHoverBuildingLevel !== null && tempHoverBuildingLevel !== level) {
            opacity = 0;
          }
        }
      }
      layers.push(
        <TileLayer
          className={styles.blurable}
          key={`Layer_${buildingId}_${level}`}
          url={levelLayer.url}
          {...LAYER_PARAMS}
          bounds={tileLayerBounds}
          id={buildingId}
          opacity={opacity}
          zIndex={1}
          updateWhenIdle={false}
          updateWhenZooming={false}
        />,
      );
    }
  } else {
    console.warn(`Level layer missing URL for building ${buildingId} at level ${level}`, levelLayer);
  }

  // Render Building Contour Polygon
  if (levelLayer?.url && levelLayer?.geo?.contour) {
    const isBlurHighlight = SHIP_LAYERS.includes(building.type);

    const eventHandlers = {
      mouseover: () => {
        playClanBaseOver();
        if (window.tooltipProvider && !selectedBuildingId) {
          tooltipTimers.current[buildingId] = setTimeout(() => {
            window.tooltipProvider?.show(<BuildingTooltip battlesStats={battlesStats} building={building} />);
          }, highlightDelay);
        }

        if (!selectedBuildingId) {
          highlightTimers.current[buildingId] = setTimeout(() => {
            setHighlightedBuilding(buildingId);
          }, highlightDelay);
        }
      },
      mouseout: () => {
        stopClanBaseOver();
        window.tooltipProvider?.hide();
        if (!selectedBuildingId) {
          clearTimeout(highlightTimers.current[buildingId]);
          clearTimeout(tooltipTimers.current[buildingId]);
          setHighlightedBuilding(null);
        }
      },
      mousemove: (e: L.LeafletMouseEvent) => {
        const cursorX = e.originalEvent.pageX - window.scrollX;
        const cursorY = e.originalEvent.pageY - window.scrollY;
        if (window.tooltipProvider && !selectedBuildingId) {
          window.tooltipProvider.update({ x: cursorX, y: cursorY });
        }
      },
      click: (e: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(e);
        selectBuilding(buildingId, !selectedBuildingId);
      },
    };

    layers.push(
      <Polygon
        key={`Building_${buildingId}_${level}_${!!selectedBuildingId}`}
        className={classNames(
          isBlurHighlight ? styles.buildingGeometryBlured : styles.buildingGeometry,
          selectedBuildingId ? styles.buildingGeometryHidden : null,
        )}
        positions={levelLayer.geo.contour}
        eventHandlers={eventHandlers}
      />,
    );
  }

  // Render Edges Polygon
  if (levelLayer?.geo?.edges && isEdgesVisible && edgePositions) {
    layers.push(<Polygon key={`Edge_${buildingId}`} positions={edgePositions} />);
  }

  // Render Marker
  const markerPosition = levelLayer?.markerPosition || null;
  if (markerPosition && !SHIP_LAYERS.includes(building.name)) {
    const isHiddenPin = selectedBuildingId && selectedBuildingId !== buildingId;

    layers.push(
      <Marker
        key={`Marker_${buildingId}`}
        icon={markers(buildingId, zoom, false)}
        position={markerPosition}
        opacity={isHiddenPin ? 0.2 : 1}
      />,
    );
  }

  return <>{layers}</>;
};

export default memo(BuildingLayer);
